import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Bell, Clock, Save, Loader2, Send, MessageSquare, RefreshCw, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { EmailAnalytics } from "./EmailAnalytics";

interface EmailSettingsData {
  id: string;
  admin_email: string;
  stage_lead_enabled: boolean;
  stage_tox_pass_activated_enabled: boolean;
  stage_consultation_booked_enabled: boolean;
  stage_payment_processed_enabled: boolean;
  stage_first_treatment_enabled: boolean;
  stage_next_appointment_enabled: boolean;
  appointment_reminder_enabled: boolean;
  appointment_reminder_hours: number;
  reengagement_enabled: boolean;
  reengagement_days_90: boolean;
  reengagement_days_120: boolean;
  reengagement_days_150: boolean;
  reengagement_offer_code_90: string | null;
  reengagement_offer_code_120: string | null;
  reengagement_offer_code_150: string | null;
}

const stageLabels: Record<string, { label: string; description: string; emailType: string }> = {
  stage_lead_enabled: { 
    label: "New Lead / Tox Pass Signup", 
    description: "When a new member signs up for a Tox Pass",
    emailType: "lead"
  },
  stage_tox_pass_activated_enabled: { 
    label: "Tox Pass Activated", 
    description: "When a Tox Pass is activated",
    emailType: "tox_pass_activated"
  },
  stage_consultation_booked_enabled: { 
    label: "Consultation Booked", 
    description: "When a member books a consultation",
    emailType: "consultation_booked"
  },
  stage_payment_processed_enabled: { 
    label: "Payment Processed", 
    description: "When a payment is completed",
    emailType: "payment_processed"
  },
  stage_first_treatment_enabled: { 
    label: "First Treatment Complete", 
    description: "When a member completes their first treatment",
    emailType: "first_treatment_complete"
  },
  stage_next_appointment_enabled: { 
    label: "Follow-up Appointment Booked", 
    description: "When a member books a follow-up appointment",
    emailType: "next_appointment_booked"
  },
};

export function EmailSettings() {
  const [settings, setSettings] = useState<EmailSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState<string | null>(null);
  const [sendingRoamTest, setSendingRoamTest] = useState(false);
  const [sendingReengagementTest, setSendingReengagementTest] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('email_settings')
        .select('*')
        .single();

      if (error) throw error;
      setSettings(data as EmailSettingsData);
    } catch (error: any) {
      console.error('Error fetching email settings:', error);
      toast.error("Failed to load email settings");
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestEmail = async (emailType: string) => {
    if (!settings?.admin_email) {
      toast.error("Please set an admin email first");
      return;
    }

    setSendingTestEmail(emailType);
    try {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          recipientEmail: settings.admin_email,
          emailType,
        },
      });

      if (error) throw error;
      toast.success(`Test email sent to ${settings.admin_email}`);
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast.error("Failed to send test email");
    } finally {
      setSendingTestEmail(null);
    }
  };

  const sendRoamTestNotification = async () => {
    setSendingRoamTest(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-roam-notification', {
        body: {
          message: `🧪 Test notification from TrueTox Admin\n\nThis is a test message sent at ${new Date().toLocaleString()}`,
          notification_type: 'test',
        },
      });

      if (error) throw error;
      toast.success("Test notification sent to Roam!");
    } catch (error: any) {
      console.error('Error sending Roam test:', error);
      toast.error("Failed to send Roam notification");
    } finally {
      setSendingRoamTest(false);
    }
  };

  const sendReengagementTestEmail = async () => {
    if (!settings?.admin_email) {
      toast.error("Please set an admin email first");
      return;
    }

    setSendingReengagementTest(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-reengagement-emails', {
        body: {
          test: true,
          testEmail: settings.admin_email,
        },
      });

      if (error) throw error;
      toast.success(`Test re-engagement email sent to ${settings.admin_email}`);
    } catch (error: any) {
      console.error('Error sending re-engagement test:', error);
      toast.error("Failed to send re-engagement test email");
    } finally {
      setSendingReengagementTest(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('email_settings')
        .update({
          admin_email: settings.admin_email,
          stage_lead_enabled: settings.stage_lead_enabled,
          stage_tox_pass_activated_enabled: settings.stage_tox_pass_activated_enabled,
          stage_consultation_booked_enabled: settings.stage_consultation_booked_enabled,
          stage_payment_processed_enabled: settings.stage_payment_processed_enabled,
          stage_first_treatment_enabled: settings.stage_first_treatment_enabled,
          stage_next_appointment_enabled: settings.stage_next_appointment_enabled,
          appointment_reminder_enabled: settings.appointment_reminder_enabled,
          appointment_reminder_hours: settings.appointment_reminder_hours,
          reengagement_enabled: settings.reengagement_enabled,
          reengagement_days_90: settings.reengagement_days_90,
          reengagement_days_120: settings.reengagement_days_120,
          reengagement_days_150: settings.reengagement_days_150,
          reengagement_offer_code_90: settings.reengagement_offer_code_90,
          reengagement_offer_code_120: settings.reengagement_offer_code_120,
          reengagement_offer_code_150: settings.reengagement_offer_code_150,
        })
        .eq('id', settings.id);

      if (error) throw error;
      toast.success("Email settings saved successfully");
    } catch (error: any) {
      console.error('Error saving email settings:', error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const updateSetting = <K extends keyof EmailSettingsData>(key: K, value: EmailSettingsData[K]) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Failed to load email settings. Please refresh the page.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs defaultValue="settings" className="space-y-6">
      <TabsList>
        <TabsTrigger value="settings" className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Settings
        </TabsTrigger>
        <TabsTrigger value="analytics" className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Analytics
        </TabsTrigger>
      </TabsList>

      <TabsContent value="settings" className="space-y-6">
      {/* Admin Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Admin Notification Email
          </CardTitle>
          <CardDescription>
            This email address will receive all admin alerts and notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email Address</Label>
            <Input
              id="admin-email"
              type="email"
              value={settings.admin_email}
              onChange={(e) => updateSetting('admin_email', e.target.value)}
              placeholder="team@truetox.com"
              className="max-w-md"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stage Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Journey Stage Notifications
          </CardTitle>
          <CardDescription>
            Choose which pipeline stages trigger email notifications to members and admins
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(stageLabels).map(([key, { label, description, emailType }]) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-border last:border-0 gap-4">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor={key} className="font-medium">{label}</Label>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendTestEmail(emailType)}
                  disabled={sendingTestEmail === emailType}
                  className="text-xs"
                >
                  {sendingTestEmail === emailType ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-3 w-3 mr-1" />
                      Test
                    </>
                  )}
                </Button>
                <Switch
                  id={key}
                  checked={settings[key as keyof EmailSettingsData] as boolean}
                  onCheckedChange={(checked) => updateSetting(key as keyof EmailSettingsData, checked as any)}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Appointment Reminders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Appointment Reminders
          </CardTitle>
          <CardDescription>
            Automatically send reminder emails before scheduled appointments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 flex-1">
              <Label htmlFor="reminder-enabled" className="font-medium">Enable Appointment Reminders</Label>
              <p className="text-sm text-muted-foreground">
                Send automated reminders to members before their appointments
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendTestEmail('appointment_reminder')}
                disabled={sendingTestEmail === 'appointment_reminder'}
                className="text-xs"
              >
                {sendingTestEmail === 'appointment_reminder' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Send className="h-3 w-3 mr-1" />
                    Test
                  </>
                )}
              </Button>
              <Switch
                id="reminder-enabled"
                checked={settings.appointment_reminder_enabled}
                onCheckedChange={(checked) => updateSetting('appointment_reminder_enabled', checked)}
              />
            </div>
          </div>

          {settings.appointment_reminder_enabled && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="reminder-hours">Send Reminder</Label>
                <Select
                  value={String(settings.appointment_reminder_hours)}
                  onValueChange={(value) => updateSetting('appointment_reminder_hours', parseInt(value))}
                >
                  <SelectTrigger className="max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">12 hours before</SelectItem>
                    <SelectItem value="24">24 hours before</SelectItem>
                    <SelectItem value="48">48 hours before</SelectItem>
                    <SelectItem value="72">72 hours before (3 days)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Members will receive an email reminder this many hours before their scheduled appointment
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Re-engagement Campaign */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Re-engagement Campaign
          </CardTitle>
          <CardDescription>
            Automatically email members who haven't visited in 90+ days with personalized win-back offers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 flex-1">
              <Label htmlFor="reengagement-enabled" className="font-medium">Enable Re-engagement Emails</Label>
              <p className="text-sm text-muted-foreground">
                Automatically send tiered win-back emails to inactive members
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={sendReengagementTestEmail}
                disabled={sendingReengagementTest}
                className="text-xs"
              >
                {sendingReengagementTest ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Send className="h-3 w-3 mr-1" />
                    Test
                  </>
                )}
              </Button>
              <Switch
                id="reengagement-enabled"
                checked={settings.reengagement_enabled}
                onCheckedChange={(checked) => updateSetting('reengagement_enabled', checked)}
              />
            </div>
          </div>

          {settings.reengagement_enabled && (
            <>
              <Separator />
              
              {/* 90-Day Tier */}
              <div className="space-y-3 p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">90-Day Gentle Nudge</Label>
                    <p className="text-sm text-muted-foreground">Friendly reminder, no discount</p>
                  </div>
                  <Switch
                    checked={settings.reengagement_days_90}
                    onCheckedChange={(checked) => updateSetting('reengagement_days_90', checked)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="offer-90" className="text-sm">Offer Code (optional)</Label>
                  <Input
                    id="offer-90"
                    value={settings.reengagement_offer_code_90 || ''}
                    onChange={(e) => updateSetting('reengagement_offer_code_90', e.target.value || null)}
                    placeholder="No discount code"
                    className="max-w-xs"
                  />
                </div>
              </div>

              {/* 120-Day Tier */}
              <div className="space-y-3 p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">120-Day Special Offer</Label>
                    <p className="text-sm text-muted-foreground">10% off incentive</p>
                  </div>
                  <Switch
                    checked={settings.reengagement_days_120}
                    onCheckedChange={(checked) => updateSetting('reengagement_days_120', checked)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="offer-120" className="text-sm">Offer Code</Label>
                  <Input
                    id="offer-120"
                    value={settings.reengagement_offer_code_120 || ''}
                    onChange={(e) => updateSetting('reengagement_offer_code_120', e.target.value || null)}
                    placeholder="COMEBACK10"
                    className="max-w-xs"
                  />
                </div>
              </div>

              {/* 150-Day Tier */}
              <div className="space-y-3 p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">150-Day Final Reminder</Label>
                    <p className="text-sm text-muted-foreground">15% off with urgency</p>
                  </div>
                  <Switch
                    checked={settings.reengagement_days_150}
                    onCheckedChange={(checked) => updateSetting('reengagement_days_150', checked)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="offer-150" className="text-sm">Offer Code</Label>
                  <Input
                    id="offer-150"
                    value={settings.reengagement_offer_code_150 || ''}
                    onChange={(e) => updateSetting('reengagement_offer_code_150', e.target.value || null)}
                    placeholder="COMEBACK15"
                    className="max-w-xs"
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Re-engagement emails run automatically once per day at 9 AM UTC. Each member only receives one email per tier.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Roam Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Roam Group Notifications
          </CardTitle>
          <CardDescription>
            Send real-time notifications to your Roam group for important events
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3">
            <div className="space-y-0.5 flex-1">
              <Label className="font-medium">Test Roam Integration</Label>
              <p className="text-sm text-muted-foreground">
                Send a test message to verify your Roam group is connected
              </p>
            </div>
            <Button
              variant="outline"
              onClick={sendRoamTestNotification}
              disabled={sendingRoamTest}
            >
              {sendingRoamTest ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Roam notifications are configured via environment secrets (ROAM_API_KEY and ROAM_GROUP_ID)
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
      </TabsContent>

      <TabsContent value="analytics">
        <EmailAnalytics />
      </TabsContent>
    </Tabs>
  );
}